import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Sparkle } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTIONS = [
  {
    key: "How long have you been together?",
    type: "choice",
    options: ["Less than a year", "1 – 3 years", "3 – 10 years", "10 years or more"],
  },
  {
    key: "How often does non-sexual touch (a hand on a shoulder, a lingering hug, holding hands) find you in a typical day?",
    type: "choice",
    options: ["Rarely, if ever", "Once or twice", "Several times", "Constantly"],
  },
  {
    key: "In your own words, what has quietly changed about physical closeness between you lately?",
    type: "text",
    placeholder: "There are no wrong answers here. A sentence is enough.",
  },
  {
    key: "On a scale from 1 (very hard) to 5 (very easy), how comfortable does it feel to talk about physical intimacy with your partner right now?",
    type: "choice",
    options: ["1 — very hard", "2", "3", "4", "5 — very easy"],
  },
  {
    key: "What are you quietly hoping for from a practice like this?",
    type: "text",
    placeholder: "One line, whatever comes to mind first.",
  },
  {
    key: "Would you feel supported by a certified therapist's guidance alongside the practice?",
    type: "choice",
    options: ["Yes — that would help", "Curious, but not sure", "Not right now — I'd like to try it privately first"],
  },
];

const PRODUCT_MAP = {
  kit: { slug: "reconnect-ritual", label: "Reconnect", price: 3499 },
  audio: { slug: "reconnect-ritual", label: "Reconnect", price: 3499 },
  therapist: { slug: "reconnect-ritual", label: "Reconnect", price: 3499 },
};

// Very light markdown renderer for **bold** and paragraphs
function renderMarkdown(text) {
  const paragraphs = text.split(/\n{2,}/);
  return paragraphs.map((para, i) => {
    const parts = para.split(/(\*\*[^*]+\*\*)/g);
    return (
      <p key={i} className="mb-5 last:mb-0 leading-relaxed">
        {parts.map((part, j) => {
          if (part.startsWith("**") && part.endsWith("**")) {
            return <strong key={j} className="font-serif text-[#4A1525] text-lg block mt-4 mb-2 first:mt-0">{part.slice(2, -2)}</strong>;
          }
          return <span key={j}>{part}</span>;
        })}
      </p>
    );
  });
}

export default function WellnessQuiz() {
  const [step, setStep] = useState(0); // 0..N-1 questions, N = submitting, N+1 = result
  const [answers, setAnswers] = useState({});
  const [textInput, setTextInput] = useState("");
  const [response, setResponse] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);

  const current = QUESTIONS[step];
  const progress = ((step) / QUESTIONS.length) * 100;

  const answer = (val) => {
    setAnswers({ ...answers, [current.key]: val });
    setTextInput("");
    if (step + 1 < QUESTIONS.length) {
      setStep(step + 1);
    } else {
      submit({ ...answers, [current.key]: val });
    }
  };

  const back = () => {
    if (step > 0) {
      setTextInput(answers[QUESTIONS[step - 1].key] || "");
      setStep(step - 1);
    }
  };

  const submit = async (finalAnswers) => {
    setStreaming(true);
    setStep(QUESTIONS.length); // loading state
    setResponse("");
    setError(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/wellness/quiz`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers: finalAnswers }),
      });
      if (!r.ok) throw new Error("Could not reach the House");
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.delta) setResponse((prev) => prev + evt.delta);
            if (evt.error) { setError(evt.error); break; }
            if (evt.done) break;
          } catch { /* ignore */ }
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setStreaming(false);
    }
  };

  // Extract product recommendation tag
  const recMatch = response.match(/\[REC:(kit|audio|therapist)\]/);
  const recommendedProduct = recMatch ? PRODUCT_MAP[recMatch[1]] : null;
  const cleanResponse = response.replace(/\[REC:(kit|audio|therapist)\]/g, "").trim();

  // Screens
  if (step === QUESTIONS.length) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 lg:py-24 min-h-[60vh] flex flex-col items-center justify-center text-center" data-testid="quiz-loading">
        <Sparkle size={40} weight="light" color="#4A1525" className="animate-pulse mb-8" />
        <h2 className="font-serif text-3xl md:text-4xl mb-4">Composing your note...</h2>
        <p className="text-[#5C5C5C]">This takes a moment — we read every answer carefully.</p>
      </div>
    );
  }

  if (step > QUESTIONS.length) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 lg:py-24" data-testid="quiz-result">
        <div className="overline text-[#4A1525] mb-4">Your note from the House</div>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-10">A recommendation, made for you.</h1>
        <div className="text-[#1A1A1A]">
          {cleanResponse ? renderMarkdown(cleanResponse) : <p className="text-[#5C5C5C]">Please wait...</p>}
        </div>
        {recommendedProduct && (
          <div className="mt-12 border border-[#4A1525] p-8 bg-[#F0EBE1]" data-testid="quiz-recommendation">
            <div className="overline text-[#4A1525] mb-2">We suggest</div>
            <h3 className="font-serif text-3xl mb-2">{recommendedProduct.label}</h3>
            <p className="text-[#5C5C5C] mb-6">₹{recommendedProduct.price}</p>
            <Link to={`/product/${recommendedProduct.slug}`} className="inline-block bg-[#4A1525] text-[#FAF8F5] px-8 py-4 overline hover:bg-[#360F1B] transition-colors" data-testid="quiz-cta">
              Discover this offering →
            </Link>
          </div>
        )}
        <div className="mt-10 flex gap-6 items-center">
          <button onClick={() => { setStep(0); setAnswers({}); setResponse(""); setError(null); }} className="overline underline underline-offset-4 text-[#5C5C5C] hover:text-[#4A1525]" data-testid="retake-quiz">
            Take again
          </button>
          <Link to="/wellness" className="overline text-[#5C5C5C] hover:text-[#4A1525]">← Back to Wellness</Link>
        </div>
        {error && <p className="text-sm text-[#7A2020] mt-6">{error}</p>}
      </div>
    );
  }

  // Auto-advance when streaming completes
  if (streaming === false && response) {
    setTimeout(() => setStep(QUESTIONS.length + 1), 0);
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-16 lg:py-24 min-h-[70vh] flex flex-col" data-testid="quiz-page">
      {/* Progress */}
      <div className="mb-16">
        <div className="flex justify-between overline text-[#5C5C5C] mb-3">
          <span>Question {step + 1} of {QUESTIONS.length}</span>
          <Link to="/wellness" className="hover:text-[#4A1525]" data-testid="quiz-exit">Exit</Link>
        </div>
        <div className="h-[1px] bg-[#E6DED5] w-full">
          <div className="h-full bg-[#4A1525] transition-all duration-500" style={{ width: `${progress}%` }} data-testid="quiz-progress" />
        </div>
      </div>

      {/* Question */}
      <div className="flex-1 fade-up" key={step}>
        <div className="overline text-[#4A1525] mb-4">A gentle question</div>
        <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-10">{current.key}</h2>

        {current.type === "choice" && (
          <div className="space-y-3" data-testid="quiz-choices">
            {current.options.map((opt) => (
              <button
                key={opt}
                onClick={() => answer(opt)}
                className="w-full text-left p-5 border border-[#E6DED5] hover:border-[#4A1525] hover:bg-[#F0EBE1] transition-colors group flex justify-between items-center"
                data-testid={`quiz-option-${opt.slice(0,10).replace(/\s+/g,'-')}`}
              >
                <span>{opt}</span>
                <ArrowRight size={16} weight="light" className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        )}

        {current.type === "text" && (
          <form onSubmit={(e) => { e.preventDefault(); if (textInput.trim()) answer(textInput.trim()); }}>
            <textarea
              autoFocus
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder={current.placeholder}
              rows={4}
              className="w-full border border-[#E6DED5] p-4 focus:outline-none focus:border-[#4A1525] bg-transparent leading-relaxed"
              data-testid="quiz-text-input"
            />
            <button type="submit" disabled={!textInput.trim()} className="mt-6 bg-[#4A1525] text-[#FAF8F5] px-8 py-4 overline hover:bg-[#360F1B] transition-colors disabled:opacity-50" data-testid="quiz-text-submit">
              Continue →
            </button>
          </form>
        )}
      </div>

      {/* Back */}
      {step > 0 && (
        <button onClick={back} className="overline text-[#5C5C5C] hover:text-[#4A1525] mt-12 self-start flex items-center gap-2" data-testid="quiz-back">
          <ArrowLeft size={14} weight="light" /> Back
        </button>
      )}
    </div>
  );
}
